// screens/Threads.tsx — B16a: the mail spine's list, and GET /api/threads'
// first consumer. B16c deepens the spread (summary, attribution, reply);
// this rung ships the list, the drill-in read, and the designed empty.
//
// The state filter chips are the SERVER's own vocabulary — the coded thread
// states (open / waiting_me / waiting_them / quiet / closed), not invented
// labels. The filter is in the hash (`/threads/:state`) so a filtered list
// is shareable/back-button-able like every route here.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  listThreads,
  type ThreadRowApi,
} from '../lib/api';
import './Threads.css';

export type ThreadStateFilter = 'all' | 'open' | 'waiting_me' | 'waiting_them' | 'quiet' | 'closed';

// Mutable StripOption[] shape — the Strip component's own contract.
const THREAD_STATES: Array<{ id: ThreadStateFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'waiting_me', label: 'On me' },
  { id: 'waiting_them', label: 'On them' },
  { id: 'quiet', label: 'Quiet' },
  { id: 'closed', label: 'Closed' },
];

export function threadStateOptions(): Array<{ id: ThreadStateFilter; label: string }> {
  return THREAD_STATES;
}

/** THE thread-state label — the filter chips, the row chips, the spread's
 * header and Today's rows all read from this one map, so a state never
 * wears two spellings ("On me" here, "waiting me" there). */
export function threadStateLabel(state: string): string {
  return THREAD_STATES.find((s) => s.id === state)?.label ?? state.replace(/_/g, ' ');
}

export interface ThreadsProps {
  tenantId: string;
  state?: string;
  onOpen: (id: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; threads: ThreadRowApi[] };

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`;
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export function Threads({ tenantId, state, onOpen }: ThreadsProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const filter = THREAD_STATES.some((s) => s.id === state) ? (state as ThreadStateFilter) : 'all';

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    listThreads(tenantId, filter === 'all' ? {} : { state: filter })
      .then((threads) => { if (!cancelled) setLoad({ phase: 'ready', threads }); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'the threads could not be read',
        });
      });
    return () => { cancelled = true; };
  }, [tenantId, filter]);

  return (
    <div class="threads">
      <div class="threads__eyebrow">Threads</div>

      {load.phase === 'loading' ? (
        <div class="threads__skeleton" aria-hidden="true">
          <div class="threads__skeleton-row" />
          <div class="threads__skeleton-row" />
          <div class="threads__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="threads__say">
          The threads could not be read.
          <br />
          <span class="threads__say-soft">{load.message}</span>
        </div>
      ) : load.threads.length === 0 ? (
        <div class="threads__say">
          {filter === 'all' ? 'No conversations yet.' : `Nothing ${labelFor(filter).toLowerCase()}.`}
          <br />
          <span class="threads__say-soft">
            {filter === 'all'
              ? 'Every email conversation lands here as one thread — read, filed, and answered in place.'
              : 'Threads move between these states as the conversation does.'}
          </span>
        </div>
      ) : (
        <div class="threads__list">
          {load.threads.map((t) => (
            <button type="button" class="threads__row" key={t.id} onClick={() => onOpen(t.id)}>
              <span class="threads__row-main">
                <span class="threads__row-subject">{t.subject ?? '(no subject)'}</span>
              </span>
              <span class="threads__row-meta">
                <span class="threads__row-state">{threadStateLabel(t.state)}</span>
                <span class="threads__row-time">{relativeTime(t.lastMessageAt)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function labelFor(id: ThreadStateFilter): string {
  return THREAD_STATES.find((s) => s.id === id)?.label ?? id;
}
